import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronUp, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

/** Painel de consumo da IEP Intelligence: custo da API x receita dos pacotes, reaproveitamento e quem mais usa. */
const AZUL = "#232D4F", VERDE = "#1E9E2A";
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const usd = (v) => `US$ ${Number(v || 0).toFixed(2)}`;
const n = (v) => Number(v || 0).toLocaleString("pt-BR");

function Kpi({ rotulo, valor, sub, cor }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{rotulo}</div>
      <div className="mt-0.5 font-display text-2xl font-bold tabular-nums" style={{ color: cor || "#0f172a" }}>{valor}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

export default function ConsumoIA() {
  const [aberto, setAberto] = useState(true);
  const [dias, setDias] = useState(30);
  const [cambio, setCambio] = useState(5.5);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!aberto) return;
    setD(null);
    api.get("/admin/innova-ia/consumo", { params: { dias, cambio } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [aberto, dias, cambio]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="ia-consumo">
      <button onClick={() => setAberto(!aberto)} className="w-full flex items-center gap-2 px-5 py-4 text-left">
        <Wallet className="h-5 w-5 text-[#1E9E2A]" />
        <span className="font-display text-lg font-bold text-slate-900">Consumo e custos</span>
        <span className="text-xs text-slate-500 hidden sm:inline">custo da API x receita dos pacotes</span>
        {aberto ? <ChevronUp className="ml-auto h-5 w-5 text-slate-400" /> : <ChevronDown className="ml-auto h-5 w-5 text-slate-400" />}
      </button>
      {aberto && (
        <div className="px-5 pb-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {[7, 30, 90, 365].map((x) => (
              <button key={x} onClick={() => setDias(x)} className={`rounded-full px-3 py-1 border text-xs font-semibold ${dias === x ? "bg-[#232D4F] text-white border-[#232D4F]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {x === 365 ? "12 meses" : `${x} dias`}
              </button>
            ))}
            <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">Dólar: R$
              <input type="number" step="0.05" min="1" max="20" value={cambio} onChange={(e) => setCambio(Number(e.target.value) || 5.5)} className="w-16 rounded-md border border-slate-200 px-2 py-1 text-slate-800" />
            </label>
          </div>
          {!d ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi rotulo="Custo da API" valor={brl(d.custo_brl)} sub={`${usd(d.custo_usd)} · ${n(d.total)} perguntas`} />
                <Kpi rotulo="Receita dos pacotes" valor={brl(d.receita_brl)} sub={`${n(d.pacotes_vendidos)} pacotes · ${n(d.creditos_vendidos)} perguntas vendidas`} cor={VERDE} />
                <Kpi rotulo="Resultado" valor={brl(d.resultado_brl)} sub="receita − custo da API (sem taxas)" cor={d.resultado_brl >= 0 ? VERDE : "#b91c1c"} />
                <Kpi rotulo="Economia" valor={brl(d.economia_brl)} sub={`${d.reaproveitamento_pct}% respondidas sem custo`} cor={VERDE} />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi rotulo="Custo médio" valor={usd(d.custo_medio_nova_usd)} sub={`por resposta nova · ${brl(d.custo_medio_nova_usd * d.cambio)}`} />
                <Kpi rotulo="Créditos usados" valor={n(d.creditos_usados)} sub="perguntas pagas com pacote" />
                <Kpi rotulo="Créditos em aberto" valor={n(d.creditos_em_aberto)} sub="saldo das alunas (hoje)" />
                <Kpi rotulo="Respostas novas" valor={n(d.tipos.find((t) => t.tipo === "nova")?.qtd)} sub="chamaram a API" />
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-800 mb-1">Perguntas por dia</div>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.serie} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="dia" tickFormatter={(x) => x.slice(8, 10) + "/" + x.slice(5, 7)} tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={18} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                      <Tooltip labelFormatter={(x) => x.split("-").reverse().join("/")} formatter={(v, k) => [v, k === "novas" ? "novas (pagas)" : "sem custo"]} />
                      <Legend formatter={(k) => (k === "novas" ? "Novas (pagas à API)" : "Sem custo (reaproveitadas/prova)")} wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="novas" stackId="a" fill={AZUL} maxBarSize={26} />
                      <Bar dataKey="reaproveitadas" stackId="a" fill={VERDE} radius={[4, 4, 0, 0]} maxBarSize={26} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-1">Quem usou</div>
                  <table className="w-full text-sm">
                    <tbody>{d.quem.map((x) => (
                      <tr key={x.quem} className="border-b border-slate-100"><td className="py-1.5 text-slate-700">{x.nome}</td>
                        <td className="text-right tabular-nums">{n(x.qtd)}</td><td className="text-right tabular-nums text-slate-500 w-24">{brl(x.usd * d.cambio)}</td></tr>))}
                    </tbody>
                  </table>
                  <div className="text-sm font-semibold text-slate-800 mt-4 mb-1">Tipo de resposta</div>
                  <table className="w-full text-sm">
                    <tbody>{d.tipos.map((x) => (
                      <tr key={x.tipo} className="border-b border-slate-100"><td className="py-1.5 text-slate-700">{x.nome}</td>
                        <td className="text-right tabular-nums">{n(x.qtd)}</td><td className="text-right tabular-nums text-slate-500 w-24">{brl(x.usd * d.cambio)}</td></tr>))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-1">Quem mais pergunta (alunas e professoras)</div>
                  {d.pessoas.length === 0 ? <p className="text-sm text-slate-500">Ninguém ainda no período.</p> : (
                    <table className="w-full text-sm">
                      <tbody>{d.pessoas.map((x) => (
                        <tr key={x.nome + x.ultima} className="border-b border-slate-100">
                          <td className="py-1.5 text-slate-700">{x.nome}{x.tipo === "professora" && <span className="ml-1.5 rounded bg-green-50 px-1.5 text-[10px] font-bold uppercase text-green-700">prof</span>}</td>
                          <td className="text-right tabular-nums">{n(x.qtd)}</td><td className="text-right tabular-nums text-slate-500 w-24">{brl(x.usd * d.cambio)}</td></tr>))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {d.frequentes.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-slate-800">Perguntas que mais se repetem</div>
                  <p className="text-xs text-slate-500 mb-1">Aprove (👍) ou corrija a resposta destas primeiro: depois de aprovadas, elas passam a sair sem custo.</p>
                  <ol className="space-y-1 text-sm">{d.frequentes.map((x) => (
                    <li key={x.pergunta} className="flex gap-2"><span className="w-7 shrink-0 text-right font-bold tabular-nums text-[#1E9E2A]">{x.qtd}×</span><span className="text-slate-700">{x.pergunta}</span></li>))}
                  </ol>
                </div>
              )}
              <p className="text-[11px] text-slate-400">Valores em reais pelo dólar informado acima. O resultado não desconta a taxa do Mercado Pago nem a comissão de professoras.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
